import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Typography,
  Button,
  Box,
  IconButton,
  TextField,
  Grid,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { Close, Save, Add, Delete, Edit, Visibility, VisibilityOff, Cancel } from '@mui/icons-material';
import { toast } from 'react-toastify';

const TokenParameterDrawer = ({ open, onClose, connectorData, masterConnectors }) => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [parameterToDelete, setParameterToDelete] = useState(null);
  const [availableParameters, setAvailableParameters] = useState([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState('');
  const [parameterValue, setParameterValue] = useState('');
  const [fieldCleared, setFieldCleared] = useState({}); // Track which fields have been cleared
  const [showPassword, setShowPassword] = useState({}); // Track visibility for encrypted fields
  const [showAddForm, setShowAddForm] = useState(false); // State to control add form visibility
  const [showEditForm, setShowEditForm] = useState(false); // State to control edit form visibility
  const [editingParameter, setEditingParameter] = useState(null); // Track which parameter is being edited
  const [editingIndex, setEditingIndex] = useState(null); // Track the index of parameter being edited
  const [savingStates, setSavingStates] = useState({}); // Track saving state for individual parameters

  // API base URLs
  const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connector-parameters`;
  const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;

  const token = localStorage.getItem('token');

  // Get the master connector name for display
  const masterConnector = masterConnectors.find(mc =>
    mc.mcRecId?.toString() === connectorData?.mcId?.toString() ||
    mc.mcId?.toString() === connectorData?.mcId?.toString()
  );

  const mcName = masterConnector ? masterConnector.mcName : 'Unknown connector';
  const masterConnectorId = connectorData?.mcId; // Master connector ID
  const connectorInstanceId = connectorData?.id; // Connector instance ID (ctd_id)
  // When Connected, credentials/client params are read-only until the user Disconnects
  const isConnected =
    String(connectorData?.status || '').trim().toLowerCase() === 'connected';

  // Debug logging
  console.log('🔍 TokenParameterDrawer - connectorData:', connectorData);
  console.log('🔍 Master Connector ID (mcId):', masterConnectorId);
  console.log('🔍 Connector Instance ID (ctd_id):', connectorInstanceId);

  // Fetch parameters and available parameters when the drawer opens
  useEffect(() => {
    if (open && connectorInstanceId) {
      fetchParameters();
      fetchAvailableParameters();
      setFieldCleared({}); // Reset field cleared state when drawer opens
      setShowPassword({}); // Reset password visibility state when drawer opens
      setShowAddForm(false); // Reset add form visibility when drawer opens
      setShowEditForm(false); // Reset edit form visibility when drawer opens
      setEditingParameter(null); // Reset editing parameter when drawer opens
      setEditingIndex(null); // Reset editing index when drawer opens
      setSavingStates({}); // Reset saving states when drawer opens
    }
  }, [open, connectorInstanceId]);

  // If status becomes Connected while drawer is open, exit any edit/add UI
  useEffect(() => {
    if (isConnected) {
      setShowAddForm(false);
      setShowEditForm(false);
      setEditingParameter(null);
      setEditingIndex(null);
      setSelectedParameter('');
      setParameterValue('');
      setDeleteDialogOpen(false);
      setParameterToDelete(null);
    }
  }, [isConnected]);

  // Fetch parameters directly in the component
  const fetchParameters = async () => {
    if (!connectorInstanceId) return;

    setLoading(true);
    try {
      // Use connector instance ID to fetch parameters (each connector gets its own)
      const response = await fetch(`${API_BASE_URL}/connector/${connectorInstanceId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Fetch available parameters to get encryptionRequired flags
      const availableParams = await fetchAvailableParameters();
      // Map parameters with encryptionRequired
      const mappedParameters = data.map(param => {
        const matchingParam = availableParams.find(ap => ap.mcpParam === param.scpName);
        const encryptionRequired = matchingParam ? matchingParam.encryptionRequired : false;
        return {
          ...param,
          encryptionRequired,
          scpValue: param.scpValue || '', // Keep scpValue as is (encrypted or plain text)
          displayValue: encryptionRequired ? '••••••••' : param.scpValue || '', // For encrypted fields, show dots initially
          hasExistingValue: encryptionRequired && param.scpValue // Track if encrypted field has existing value
        };
      });
      setParameters(data.length > 0 ? mappedParameters : []);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error(`Error fetching parameters: ${error.message}`);
      setParameters([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch available parameters for this connector
  const fetchAvailableParameters = async () => {
    if (!masterConnectorId) return [];

    setLoadingParameters(true);
    try {
      const response = await fetch(`${MASTER_CONNECTOR_API_URL}/${masterConnectorId}/parameters`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAvailableParameters(data);
      return data; // Return data for use in fetchParameters
    } catch (error) {
      console.error('Error fetching available parameters:', error);
      toast.error(`Error fetching available parameters: ${error.message}`);
      setAvailableParameters([]);
      return [];
    } finally {
      setLoadingParameters(false);
    }
  };

  const createEmptyParameter = (name = '') => ({
    id: null,
    scpId: null,
    scpCtdId: connectorInstanceId, // Use connector instance ID (each connector gets unique params)
    scpName: name,
    scpValue: '',
    displayValue: '', // Add displayValue for editing
    encryptionRequired: false, // Default, will be updated when selected
    hasExistingValue: false // New parameters don't have existing values
  });

  const handleAddParameter = () => {
    if (isConnected) {
      toast.warning('Disconnect this account before adding or changing credentials.');
      return;
    }
    if (!showAddForm) {
      fetchAvailableParameters();
    }
    setShowAddForm(!showAddForm);
    setSelectedParameter('');
    setParameterValue('');
    // Close edit form if open
    setShowEditForm(false);
    setEditingParameter(null);
    setEditingIndex(null);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setSelectedParameter('');
    setParameterValue('');
  };

  // Individual save function for adding new parameter
  const handleSaveNewParameter = async () => {
    if (isConnected) {
      toast.warning('Disconnect this account before adding or changing credentials.');
      return;
    }
    if (!selectedParameter) {
      toast.error('Please select a parameter');
      return;
    }

    // Check if parameter already exists
    const paramExists = parameters.some(p => p.scpName === selectedParameter);
    if (paramExists) {
      toast.warning('This parameter already exists');
      return;
    }

    setSavingStates(prev => ({ ...prev, add: true }));

    try {
      const matchingParam = availableParameters.find(ap => ap.mcpParam === selectedParameter);
      const encryptionRequired = matchingParam ? matchingParam.encryptionRequired : false;

      const valueToSave = parameterValue;

      const paramToSave = {
        scpCtdId: connectorInstanceId, // Use connector instance ID
        scpMcId: masterConnectorId,     // Use master connector ID
        scpName: selectedParameter,
        scpValue: valueToSave
      };

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify([paramToSave]),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const savedData = await response.json();
      const newParam = {
        ...savedData[0],
        encryptionRequired,
        displayValue: encryptionRequired ? '••••••••' : parameterValue,
        hasExistingValue: encryptionRequired && savedData[0].scpValue
      };

      setParameters([...parameters, newParam]);
      setShowAddForm(false);
      setSelectedParameter('');
      setParameterValue('');
      toast.success('Parameter saved successfully');
    } catch (error) {
      console.error('Error saving parameter:', error);
      toast.error(`Error saving parameter: ${error.message}`);
    } finally {
      setSavingStates(prev => ({ ...prev, add: false }));
    }
  };

  // Handle edit parameter
  const handleEditParameter = (index) => {
    if (isConnected) {
      toast.warning('Disconnect this account before editing credentials.');
      return;
    }
    const param = parameters[index];
    setEditingParameter(param);
    setEditingIndex(index);
    setSelectedParameter(param.scpName);
    // For encrypted fields, clear the parameter value text field
    // For non-encrypted fields, show the existing value
    setParameterValue(param.encryptionRequired ? '' : (param.displayValue || param.scpValue || ''));
    setShowEditForm(true);
    // Close add form if open
    setShowAddForm(false);
    
    // Fetch available parameters to populate dropdown
    if (availableParameters.length === 0) {
      fetchAvailableParameters();
    }
  };

  const handleCancelEdit = () => {
    setShowEditForm(false);
    setEditingParameter(null);
    setEditingIndex(null);
    setSelectedParameter('');
    setParameterValue('');
  };

  // Individual save function for editing existing parameter
  const handleSaveEditParameter = async () => {
    if (isConnected) {
      toast.warning('Disconnect this account before editing credentials.');
      return;
    }
    if (!selectedParameter) {
      toast.error('Please select a parameter');
      return;
    }

    // Check if parameter name changed and already exists (excluding current parameter)
    if (selectedParameter !== editingParameter.scpName) {
      const paramExists = parameters.some((p, idx) => p.scpName === selectedParameter && idx !== editingIndex);
      if (paramExists) {
        toast.warning('This parameter already exists');
        return;
      }
    }

    setSavingStates(prev => ({ ...prev, [`edit-${editingIndex}`]: true }));

    try {
      const matchingParam = availableParameters.find(ap => ap.mcpParam === selectedParameter);
      const encryptionRequired = matchingParam ? matchingParam.encryptionRequired : false;

      let valueToSave;
      if (encryptionRequired) {
        if (parameterValue) {
          // New value entered as plaintext; backend handles encryption.
          valueToSave = parameterValue;
        } else if (editingParameter.hasExistingValue) {
          // No new value, keep existing encrypted value
          valueToSave = editingParameter.scpValue;
        } else {
          valueToSave = '';
        }
      } else {
        // Non-encrypted, use parameter value
        valueToSave = parameterValue;
      }

      const paramToSave = {
        ...editingParameter,
        scpName: selectedParameter,
        scpValue: valueToSave
      };

      const response = await fetch(`${API_BASE_URL}/${paramToSave.scpId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(paramToSave),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const savedParam = await response.json();
      const updatedParam = {
        ...savedParam,
        encryptionRequired,
        displayValue: encryptionRequired ? '••••••••' : parameterValue,
        hasExistingValue: encryptionRequired && savedParam.scpValue
      };

      const updatedParameters = [...parameters];
      updatedParameters[editingIndex] = updatedParam;
      setParameters(updatedParameters);

      setShowEditForm(false);
      setEditingParameter(null);
      setEditingIndex(null);
      setSelectedParameter('');
      setParameterValue('');
      toast.success('Parameter updated successfully');
    } catch (error) {
      console.error('Error updating parameter:', error);
      toast.error(`Error updating parameter: ${error.message}`);
    } finally {
      setSavingStates(prev => ({ ...prev, [`edit-${editingIndex}`]: false }));
    }
  };

  const handleRemoveParameter = (index) => {
    if (isConnected) {
      toast.warning('Disconnect this account before changing credentials.');
      return;
    }
    setParameterToDelete({ index, param: parameters[index] });
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (isConnected) {
      toast.warning('Disconnect this account before changing credentials.');
      setDeleteDialogOpen(false);
      setParameterToDelete(null);
      return;
    }
    const { index, param } = parameterToDelete;
    const newParameters = [...parameters];
    newParameters.splice(index, 1);
    setParameters(newParameters);

    if (param.scpId) {
      try {
        const response = await fetch(`${API_BASE_URL}/${param.scpId}`, {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        toast.success('Parameter deleted successfully');
        await fetchParameters();
      } catch (error) {
        console.error('Error deleting parameter:', error);
        toast.error(`Error deleting parameter: ${error.message}`);
        setParameters([...parameters]); // Revert state on error
      }
    }

    setDeleteDialogOpen(false);
    setParameterToDelete(null);
  };

  // Toggle password visibility
  const togglePasswordVisibility = (index) => {
    setShowPassword(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Get display value for encrypted fields based on visibility state
  const getDisplayValue = (param, index) => {
    if (!param.encryptionRequired) {
      return param.displayValue !== undefined ? param.displayValue : param.scpValue || '';
    }

    // For encrypted fields
    if (showPassword[index]) {
      // Show actual value when eye is clicked
      if (param.hasExistingValue && !fieldCleared[index]) {
        return '[Encrypted Value]'; // Show placeholder for existing encrypted data
      }
      return param.displayValue || '';
    } else {
      // Show dots or actual value based on state
      return param.displayValue !== undefined ? param.displayValue : param.scpValue || '';
    }
  };

  // Handle focus on parameter value field - REMOVED since field is now disabled
  const handleParameterFocus = (index) => {
    // This function is kept for potential future use but no longer needed
    // since the parameter value field is disabled in display mode
  };

  const handleParameterChange = (index, field, value) => {
    // This function is kept for potential future use but no longer needed
    // since the parameter value field is disabled in display mode
    const newParameters = [...parameters];
    if (field === 'scpValue') {
      // Update displayValue for editing (keep as plain text during editing)
      newParameters[index] = {
        ...newParameters[index],
        displayValue: value
      };
    } else {
      newParameters[index] = {
        ...newParameters[index],
        [field]: value
      };
    }
    setParameters(newParameters);
  };

  // Get the display name for a parameter object
  const getParameterName = (param) => {
    if (typeof param === 'string') {
      return param;
    } else if (param?.mcpParam) {
      return param.mcpParam;
    } else if (param?.paramName) {
      return param.paramName;
    } else if (param?.name) {
      return param.name;
    } else {
      return 'Unknown parameter';
    }
  };

  // Get a unique identifier for a parameter object
  const getParameterValue = (param) => {
    if (typeof param === 'string') {
      return param;
    } else if (param?.mcpParam) {
      return param.mcpParam;
    } else if (param?.paramName) {
      return param.paramName;
    } else if (param?.name) {
      return param.name;
    } else if (param?.mcpRecId) {
      return param.mcpRecId.toString();
    } else {
      return JSON.stringify(param);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '500px',
            padding: 2,
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Token Parameters</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {connectorData && (
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Connector Details</Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="Master Connector"
                  secondary={mcName}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Connection Status"
                  secondary={connectorData.status || 'Unknown'}
                />
              </ListItem>
            </List>
          </Paper>
        )}

        {isConnected && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This account is connected. Disconnect it first to add, edit, or delete credentials (client ID, secret, etc.).
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1">Parameters</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!showAddForm && !showEditForm && (
                  <Button
                    startIcon={<Add />}
                    onClick={handleAddParameter}
                    variant="outlined"
                    size="small"
                    color="primary"
                    disabled={isConnected}
                  >
                    Add Parameter
                  </Button>
                )}
              </Box>
            </Box>

            {/* Add Parameter Form */}
            {showAddForm && !isConnected && (
              <Paper
                elevation={2}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '2px dashed',
                  borderColor: 'primary.main',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  Add New Parameter
                </Typography>
                {loadingParameters ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="parameter-select-label">Select Parameter</InputLabel>
                        <Select
                          labelId="parameter-select-label"
                          id="parameter-select"
                          value={selectedParameter}
                          label="Select Parameter"
                          onChange={(e) => setSelectedParameter(e.target.value)}
                        >
                          {availableParameters.length === 0 ? (
                            <MenuItem disabled>No available parameters</MenuItem>
                          ) : (
                            availableParameters.map((param, index) => (
                              <MenuItem key={index} value={getParameterValue(param)}>
                                {getParameterName(param)}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Parameter Value"
                        value={parameterValue}
                        onChange={(e) => setParameterValue(e.target.value)}
                        fullWidth
                        size="small"
                        margin="dense"
                        type="text"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          onClick={handleCancelAdd}
                          size="small"
                          variant="outlined"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveNewParameter}
                          variant="contained"
                          color="primary"
                          size="small"
                          disabled={!selectedParameter || savingStates.add}
                          startIcon={savingStates.add ? <CircularProgress size={16} /> : <Save />}
                        >
                          {savingStates.add ? 'Saving...' : 'Save'}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Paper>
            )}

            {parameters.length === 0 && !showAddForm && !showEditForm ? (
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">
                  {isConnected
                    ? 'No parameters configured. Disconnect this account to add credentials.'
                    : "No parameters added yet. Click 'Add Parameter' to select from available parameters."}
                </Typography>
              </Paper>
            ) : (
              parameters.map((param, index) => (
                // Show edit form for the parameter being edited, otherwise show normal parameter display
                showEditForm && editingIndex === index && !isConnected ? (
                  <Paper
                    key={param.scpId || `param-${index}`}
                    elevation={2}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: '2px dashed',
                      borderColor: 'primary.main',
                      // backgroundColor: 'rgba(237, 108, 2, 0.04)'
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
                      Edit Parameter
                    </Typography>
                    {loadingParameters ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth size="small">
                            <InputLabel id="edit-parameter-select-label">Select Parameter</InputLabel>
                            <Select
                              labelId="edit-parameter-select-label"
                              id="edit-parameter-select"
                              value={selectedParameter}
                              label="Select Parameter"
                              onChange={(e) => setSelectedParameter(e.target.value)}
                            >
                              {availableParameters.length === 0 ? (
                                <MenuItem disabled>No available parameters</MenuItem>
                              ) : (
                                availableParameters.map((availableParam, availableIndex) => (
                                  <MenuItem key={availableIndex} value={getParameterValue(availableParam)}>
                                    {getParameterName(availableParam)}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            label="Parameter Value"
                            value={parameterValue}
                            onChange={(e) => setParameterValue(e.target.value)}
                            fullWidth
                            size="small"
                            margin="dense"
                            type="text"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                              onClick={handleCancelEdit}
                              size="small"
                              variant="outlined"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleSaveEditParameter}
                              variant="contained"
                              color="primary"
                              size="small"
                              disabled={!selectedParameter || savingStates[`edit-${editingIndex}`]}
                              startIcon={savingStates[`edit-${editingIndex}`] ? <CircularProgress size={16} /> : <Save />}
                            >
                              {savingStates[`edit-${editingIndex}`] ? 'Saving...' : 'Save'}
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Paper>
                ) : (
                  <Paper
                    key={param.scpId || `param-${index}`}
                    elevation={1}
                    sx={{ p: 2, mb: 2, position: 'relative' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditParameter(index)}
                        disabled={isConnected || showAddForm || showEditForm}
                        title={isConnected ? 'Disconnect before editing credentials' : 'Edit parameter'}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveParameter(index)}
                        disabled={isConnected || showAddForm || showEditForm}
                        title={isConnected ? 'Disconnect before changing credentials' : 'Delete parameter'}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Parameter Name"
                          value={param.scpName || ''}
                          disabled
                          fullWidth
                          size="small"
                          margin="dense"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ position: 'relative' }}>
                          <TextField
                            label="Parameter Value"
                            value={getDisplayValue(param, index)}
                            disabled={true} // Always disabled - can only be edited through edit form
                            fullWidth
                            required
                            size="small"
                            margin="dense"
                            type={param.encryptionRequired && !showPassword[index] ? 'password' : 'text'}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )
              ))
            )}
          </>
        )}
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this parameter?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default TokenParameterDrawer;